import { useEffect, useRef } from 'react';
import type { Newsletter } from '@/types';
import { useAppDispatch } from '@/store/hooks';
import { saveNewsletterThunk } from '@/store/slices/newslettersSlice';
import { setAutosaveStatus } from '@/store/slices/uiSlice';
import { useDebounce } from '@/hooks/useDebounce';

const AUTOSAVE_DELAY_MS = 1500;

/** Debounces newsletter edits and persists them via saveNewsletterThunk once they settle. */
export function useAutosave(newsletter: Newsletter | null, enabled: boolean) {
  const dispatch = useAppDispatch();
  const debounced = useDebounce(newsletter, AUTOSAVE_DELAY_MS);
  const lastSavedJson = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !debounced) return;
    const json = JSON.stringify(debounced);
    if (json === lastSavedJson.current) return;

    let cancelled = false;
    dispatch(setAutosaveStatus('saving'));
    dispatch(saveNewsletterThunk(debounced))
      .unwrap()
      .then(() => {
        if (cancelled) return;
        lastSavedJson.current = json;
        dispatch(setAutosaveStatus('saved'));
      })
      .catch(() => {
        if (!cancelled) dispatch(setAutosaveStatus('error'));
      });

    return () => {
      cancelled = true;
    };
  }, [debounced, enabled, dispatch]);
}
